import React, { useCallback, useMemo } from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import { toShort } from '@tradingblock/api';
import {
  DefaultAccountDetails,
  AccountType,
  CashieringDetailRelationship,
  AchRelationship,
  BankRiskScoreValues,
} from '@tradingblock/types';
import { useStateSelector } from '../../../../data/global/dataSelectors';
import { accountSelectors, accountDetailsSelectors } from '../../../../data/global/selectors/admin/accountSelectors';
import { ValueArray } from '../../../../components/basic/ValueArray';
import { DetailField, DetailFields } from '../../../../components/basic/DetailFields';
import { formatPhoneNumber } from '../../../../utilities/accountManagement';
import { Number } from '@tradingblock/components';
import styled from 'styled-components';

interface CustomerAccountDetailsProps {
  accountId: number;
  relationship?: AchRelationship;
  relationshipDetails?: CashieringDetailRelationship;
  useCardLayout?: boolean; // Add prop for card layout
}

// Styled components matching the admin cashiering detail component pattern
const SectionCard = styled.div`
  background: var(--blue-dark);
  border-radius: var(--radius);
  border: 1px solid var(--lighten);
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const SectionHeader = styled.div`
  background: var(--blue-dark);
  padding: var(--gap);
  border-bottom: 1px solid var(--lighten);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;

  h3 {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 300;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 0.5em;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    padding-left: 0;
  }
`;

const SectionContent = styled.div`
  background: var(--blue-dark);
  padding: var(--gap);
  color: #fff;

  /* Consistent text styling */
  label {
    font-weight: 600;
    color: #fff;
    margin-right: 0.5rem;
    font-size: 0.98rem;
  }

  .mute {
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.98rem;
    font-weight: 400;
  }

  /* Better spacing */
  > div {
    margin-bottom: 0;
  }

  > div:last-child {
    margin-bottom: 0;
  }

  /* Ensure consistent value styling */
  span:not(.mute):not([style*='color: var(--teal)']):not([style*='color: var(--orange)']):not([style*='color: var(--green')]) {
    color: rgba(255, 255, 255, 0.85);
    font-weight: 400;
  }

  /* Consistent fields-title styling */
  .fields-title {
    color: #fff;
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  /* Override prop-title and prop-value styling for cashiering */
  .prop-title {
    font-size: 0.98rem;
    font-weight: 600;
    color: #fff;
    margin-right: 0.5rem;
  }

  .prop-value {
    font-size: 0.98rem;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.4;
  }

  /* Ensure prop-stacked layout works correctly */
  .prop-stacked {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
`;

const ResponsiveGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--gap);
  width: 100%;
  margin: 0;
`;

const AccountInfoHeader = styled.div`
  padding: 0 0 var(--gap) 0;
  border-bottom: 1px solid var(--lighten);
  margin-bottom: var(--gap);

  h3 {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 300;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 0.5em;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }
`;

const AccountInfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--gap);
`;

const GridHeader = styled.div`
  grid-column: 1 / -1;
  font-size: 1.1rem;
  font-weight: bold;
  color: #fff;
  margin-bottom: 0.5rem;
`;

const DetailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--gap);
  margin-top: var(--gap);
  margin-bottom: var(--gap);
`;

const StatusIndicator = styled.span<{ status: 'pos' | 'neg' | 'mute' }>`
  font-weight: 500;
  color: ${props => {
    switch (props.status) {
      case 'pos':
        return '#2ecc40'; // green
      case 'neg':
        return '#ff4136'; // red
      case 'mute':
      default:
        return 'rgba(255, 255, 255, 0.85)';
    }
  }};
`;

const WarningText = styled.div`
  font-size: 0.75rem;
  color: var(--orange);
  margin-top: calc(var(--gap) / 4);
`;

const LastUpdatedSection = styled.div`
  margin-top: var(--gap);
  padding-top: var(--gap);
  border-top: 1px solid var(--lighten);
`;

const LastUpdatedTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: calc(var(--gap) / 2);
  color: var(--teal);
`;

const LastUpdatedItem = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: calc(var(--gap) / 4);
`;

// Add field title and value styling
const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0;
  padding: 0;

  .prop-title {
    font-size: 0.9rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 0.25rem;
  }

  .prop-value {
    font-size: 0.9rem;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.4;
  }
`;

// More compact field title/value
const FieldTitle = styled.div`
  font-size: 0.98rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 0.1rem;
  text-align: left;
`;

const FieldValue = styled.div`
  font-size: 0.98rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.85);
  text-align: left;
`;

export const CustomerAccountDetails: React.FunctionComponent<CustomerAccountDetailsProps> = ({
  accountId,
  relationship,
  relationshipDetails,
  useCardLayout = false,
}) => {
  const account = useStateSelector(s => accountSelectors.data(s, accountId));
  const accountDetails = useStateSelector(s => accountDetailsSelectors.data(s, accountId));

  const {
    accountNames,
    mailingAddress,
    physicalAddress,
    email,
    homePhone,
    mobilePhone,
    workPhone,
    lastChanged,
    repFirstName,
    repLastName,
    repPhoneNumber,
    accountStatus,
  } = accountDetails || DefaultAccountDetails;

  const getAccountStatus = useCallback((status: string) => {
    const formattedStatus = status.replace(/([A-Z])/g, ' $1').trim();
    switch (status) {
      case 'Active':
        return <StatusIndicator status="pos">{formattedStatus}</StatusIndicator>;
      case 'Inactive':
      case 'Closed':
        return <StatusIndicator status="neg">{formattedStatus}</StatusIndicator>;
      default:
        return <StatusIndicator status="mute">{formattedStatus}</StatusIndicator>;
    }
  }, []);

  const getBankRiskScore = useCallback((score?: BankRiskScoreValues) => {
    const formattedRiskScore = score && score.replace(/([A-Z])/g, ' $1').trim();

    switch (score) {
      case BankRiskScoreValues.LowRisk:
        return (
          <>
            <span>Bank Risk Score:</span>
            <p className="low-risk-score" style={{ fontWeight: 'bold' }}>
              {formattedRiskScore} <i className="fal fa-laugh-beam" />
            </p>
          </>
        );
      case BankRiskScoreValues.MediumRisk:
        return (
          <>
            <span>Bank Risk Score:</span>
            <p className="medium-risk-score" style={{ fontWeight: 'bold' }}>
              {formattedRiskScore} <i className="fal fa-meh" />
            </p>
          </>
        );
      case BankRiskScoreValues.HighRisk:
        return (
          <>
            <span>Bank Risk Score:</span>
            <p className="high-risk-score" style={{ fontWeight: 'bold' }}>
              {formattedRiskScore} <i className="fal fa-frown" />
            </p>
          </>
        );
      default:
        return null;
    }
  }, []);

  const formatLastUpdated = useCallback((lastUpdated: Date | null | undefined, label: string) => {
    const isRecent = lastUpdated && dayjs(lastUpdated).isAfter(dayjs().subtract(2, 'week'));
    return (
      <LastUpdatedItem className={isRecent ? 'neg' : 'mute'}>
        {label}: {lastUpdated ? toShort(lastUpdated) : _.isUndefined(lastUpdated) ? 'Unknown' : 'Never'}
      </LastUpdatedItem>
    );
  }, []);

  const fields = useMemo(() => {
    let fieldList: DetailField[] = [];
    if (account) {
      fieldList = [
        ...fieldList,
        { Value: () => <>TB Account &amp; Customer Information</>, className: 'fields-title' },
        { Value: () => <>Bank Account Information</>, hide: !relationship, className: 'fields-title' },

        {
          title: 'Account title',
          Value: () => <div className="mute mb-3">{account.AccountTitle}</div>,
        },
        {
          title: 'Bank name',
          Value: () => <div className="mute mb-3">{relationship && relationship.bankName}</div>,
          hide: !relationship,
        },

        { title: 'Account number', Value: () => <div className="mute mb-3">{account.AccountNumber}</div> },
        {
          title: 'Bank account number',
          Value: () => (
            <div className="mb-3">
              <div className="mute mb-3">{relationship && relationship.bankAccount}</div>
              {relationshipDetails && relationshipDetails.bankAccountBalance && (
                <>
                  <div className="mt-3" style={{ fontWeight: 600, color: '#fff' }}>
                    Balance
                  </div>
                  <div className="mute">
                    <Number value={relationshipDetails.bankAccountBalance} currency />
                  </div>
                </>
              )}
            </div>
          ),
          hide: !relationship,
        },
        {
          title: 'Account status',
          Value: () => getAccountStatus(accountStatus || 'Unknown'),
        },
        {
          title: 'Recent activity',
          Value: () => (
            <div className="mb-3">
              <div className="mute">
                {relationship && formatLastUpdated(relationship.lastModifiedDate, 'Bank info last modified')}
              </div>
              {relationship && relationship.approvalMethod === 'Plaid' && relationshipDetails && (
                <div>
                  <div className="mute">
                    Last transaction:{' '}
                    {relationshipDetails.bankAccountLastTransactionDate
                      ? dayjs(relationshipDetails.bankAccountLastTransactionDate).format('MMM D, YYYY')
                      : 'No transactions in the last 30 days.'}
                  </div>
                </div>
              )}
            </div>
          ),
          hide: !relationship,
        },
        {
          title: 'Rep name',
          Value: () => (
            <div className="mute mb-3">
              {repFirstName} {repLastName || 'None'}
            </div>
          ),
        },
        {
          title: 'Approval method',
          Value: () => <div className="mute mb-3">{relationship && relationship.approvalMethod}</div>,
          hide: !relationship,
        },
        { title: 'Rep code', Value: () => <div className="mute mb-3">{account.RepCode || 'None'}</div> },
        {
          title: 'Status Approved',
          Value: () => {
            if (relationship && !relationship.approval) {
              return <div className="mute mb-3">N/A</div>;
            } else if (relationship && relationship.approval) {
              return <div className="mute mb-3">{dayjs(relationship.approval.approvalTime).format('MMM D, YYYY')}</div>;
            }
          },
          hide: !relationship,
        },
        {
          title: 'Rep Phone Number',
          Value: () => <div className="mute mb-3">{formatPhoneNumber(repPhoneNumber)}</div>,
        },
        {
          Value: () => getBankRiskScore(relationshipDetails && relationshipDetails.bankRiskScore),
          hide: !relationshipDetails,
        },
        {
          title: 'Account type',
          Value: () => <div className="mute mb-3">{account.AccountType && AccountType[account.AccountType]}</div>,
        },
        {
          title: 'Bank account nickname',
          Value: () => <div className="mute mb-3">{relationship && relationship.nickName}</div>,
          hide: !relationship,
        },
        {
          title: 'Account owner name(s)',
          Value: () => {
            let hasAllOwners;
            const isMatch = accountNames.some(name => {
              const upperName = name.toUpperCase();
              const upperSecret =
                relationshipDetails && relationshipDetails.bankAccountOwnerNameSecret
                  ? relationshipDetails.bankAccountOwnerNameSecret.toUpperCase()
                  : '';
              const nameWords = upperName.split(' ').filter(word => word.length > 1);
              const secretWords = upperSecret.split(' ').filter(word => word.length > 1);
              hasAllOwners = nameWords.every(word => secretWords.includes(word));
              return hasAllOwners;
            });
            const status = isMatch ? 'pos' : !relationship ? 'mute' : 'neg';
            return (
              <>
                <div className={`${status} mb-3`}>{_.join(accountNames, ' / ')}</div>
                {!hasAllOwners && relationshipDetails && <WarningText>*Review Owners/Signatories*</WarningText>}
              </>
            );
          },
        },
        {
          title: 'Bank account signatories',
          Value: () => {
            const isMatch = accountNames.some(name => {
              const upperName = name.toUpperCase();
              const upperSecret =
                relationshipDetails && relationshipDetails.bankAccountOwnerNameSecret
                  ? relationshipDetails.bankAccountOwnerNameSecret.toUpperCase()
                  : '';
              const nameWords = upperName.split(' ').filter(word => word.length > 1);
              const secretWords = upperSecret.split(' ').filter(word => word.length > 1);
              const hasAllOwners = nameWords.every(word => secretWords.includes(word));
              return hasAllOwners;
            });

            const status = isMatch ? 'pos' : 'neg';
            return (
              <div className={`${status} mb-3`}>
                {relationshipDetails && _.startCase(_.toLower(relationshipDetails.bankAccountOwnerNameSecret))}
              </div>
            );
          },
          hide: !relationship,
        },
        {
          title: 'Physical address',
          Value: () => (
            <div className="mute mb-3">
              {physicalAddress ? (
                <div>
                  {physicalAddress.address1 && <div>{physicalAddress.address1}</div>}
                  {physicalAddress.address2 && <div>{physicalAddress.address2}</div>}
                  <div>
                    {physicalAddress.city}
                    {physicalAddress.state ? ', ' + physicalAddress.state : ''} {physicalAddress.postalCode}
                  </div>
                  <div> {physicalAddress.country}</div>
                </div>
              ) : (
                'none'
              )}
            </div>
          ),
        },
        {
          title: 'Primary address',
          Value: () => (
            <div className="mute mb-3">
              {relationshipDetails && relationshipDetails.reviewAddresses && (
                <div>
                  {relationshipDetails.reviewAddresses[0].street && (
                    <div>{relationshipDetails.reviewAddresses[0].street}</div>
                  )}
                  <div>
                    {relationshipDetails.reviewAddresses[0].city}
                    {relationshipDetails.reviewAddresses[0].stateOrRegion
                      ? ', ' + relationshipDetails.reviewAddresses[0].stateOrRegion
                      : ''}{' '}
                    {relationshipDetails.reviewAddresses[0].postalCode}
                    <div>{relationshipDetails.reviewAddresses[0].country}</div>
                  </div>
                </div>
              )}
            </div>
          ),
          hide: !relationship,
        },
        {
          title: 'Mailing address',
          Value: () => (
            <div className="mute mb-3">
              {mailingAddress ? (
                <div>
                  {mailingAddress.address1 && <div>{mailingAddress.address1}</div>}
                  {mailingAddress.address2 && <div>{mailingAddress.address2}</div>}
                  <div>
                    {mailingAddress.city}
                    {mailingAddress.state ? ', ' + mailingAddress.state : ''} {mailingAddress.postalCode}
                  </div>
                  <div>{mailingAddress.country}</div>
                </div>
              ) : (
                'none'
              )}
            </div>
          ),
        },
        {
          title: 'Secondary address',
          Value: () => (
            <div className="mute mb-3">
              {relationshipDetails && relationshipDetails.reviewAddresses && relationshipDetails.reviewAddresses[1] && (
                <div>
                  {relationshipDetails.reviewAddresses[1].street && (
                    <div>{relationshipDetails.reviewAddresses[1].street}</div>
                  )}
                  <div>
                    {relationshipDetails.reviewAddresses[1].city}
                    {relationshipDetails.reviewAddresses[1].stateOrRegion
                      ? ', ' + relationshipDetails.reviewAddresses[1].stateOrRegion
                      : ''}{' '}
                    {relationshipDetails.reviewAddresses[1].postalCode}
                  </div>
                  <div>{relationshipDetails.reviewAddresses[1].country}</div>
                </div>
              )}
            </div>
          ),
          hide: !relationship,
        },
        {
          title: 'Email address',
          Value: () => <div className="mute mb-3">{email ? <a href={`mailto:${email}`}>{email}</a> : <>none</>}</div>,
        },
        {
          title: 'Relationship Email address',
          Value: () => (
            <div className="mute mb-3">
              {relationshipDetails && (
                <ValueArray
                  values={relationshipDetails.reviewEmails}
                  renderer={email => <a href={`mailto:${email}`}>{email}</a>}
                  delimiter=" / "
                />
              )}
            </div>
          ),
          hide: !relationship,
        },
        {
          title: 'Phone',
          Value: () => (
            <div className="mute mb-3">
              {homePhone && (
                <div className="mb-3">
                  <a href={`tel:${homePhone}`}>{homePhone}</a> (home)
                </div>
              )}
              {workPhone && (
                <div className="mb-3">
                  <a href={`tel:${workPhone}`}>{workPhone}</a> (work)
                </div>
              )}
              {mobilePhone && (
                <div className="mb-3">
                  <a href={`tel:${mobilePhone}`}>{mobilePhone}</a> (mobile)
                </div>
              )}
            </div>
          ),
        },
        {
          title: 'Relationship Phone',
          Value: () => (
            <div className="mute mb-3">
              {relationshipDetails && (
                <ValueArray
                  values={relationshipDetails.reviewPhoneNumbers}
                  renderer={phone => <a href={`tel:${phone}`}>{phone}</a>}
                  delimiter=" / "
                />
              )}
            </div>
          ),
          hide: !relationship,
        },
      ];
    }
    return fieldList;
  }, [
    account,
    accountNames,
    mailingAddress,
    physicalAddress,
    email,
    homePhone,
    mobilePhone,
    workPhone,
    relationship,
    relationshipDetails,
    getAccountStatus,
    getBankRiskScore,
    formatLastUpdated,
  ]);

  // Find and extract the section header (fields-title)
  const headerField = fields.find(f => f.className === 'fields-title');
  // Filter out hidden fields and section headers before pairing
  const otherFields = fields.filter(f => f.className !== 'fields-title' && !f.hide);
  // Dynamically pair all other fields for two-column layout
  const fieldPairs = [];
  for (let i = 0; i < otherFields.length; i += 2) {
    fieldPairs.push([otherFields[i], otherFields[i + 1]]);
  }

  if (!account && !accountDetails) {
    return null;
  }

  if (useCardLayout) {
    return (
      <div className="section-body">
        <AccountInfoHeader>
          <h3 className="mt-5">
            <i className="fal fa-user" style={{ marginRight: '8px' }}></i>
            TB Account & Customer Information
          </h3>
        </AccountInfoHeader>
        <AccountInfoContent>
          <ResponsiveGrid>
            {fieldPairs.flatMap((pair, i) => {
              const items = [];
              if (pair[0]) {
                items.push(
                  <FieldGroup key={`field-${i}-0`}>
                    <DetailFields fieldList={[pair[0]]} valueAs="div" />
                  </FieldGroup>
                );
              }
              if (pair[1]) {
                items.push(
                  <FieldGroup key={`field-${i}-1`}>
                    <DetailFields fieldList={[pair[1]]} valueAs="div" />
                  </FieldGroup>
                );
              }
              return items;
            })}
          </ResponsiveGrid>
          {lastChanged && (
            <LastUpdatedSection>
              <LastUpdatedTitle>Last updated contacts</LastUpdatedTitle>
              <div className="mute">
                {formatLastUpdated(lastChanged.email, 'Email address')}
                {formatLastUpdated(lastChanged.phone, 'Phone number')}
                {formatLastUpdated(lastChanged.address, 'Mailing address(es)')}
              </div>
            </LastUpdatedSection>
          )}
        </AccountInfoContent>
      </div>
    );
  }

  // Legacy layout
  return (
    <>
      <ResponsiveGrid style={{ marginTop: 0, marginBottom: 0 }}>
        {headerField && (
          <GridHeader>
            <DetailFields fieldList={[headerField]} valueAs="div" />
          </GridHeader>
        )}
        {fieldPairs.map((pair, i) => (
          <React.Fragment key={i}>
            {pair[0] && (
              <FieldGroup>
                <DetailFields fieldList={[pair[0]]} valueAs="div" />
              </FieldGroup>
            )}
            {pair[1] && (
              <FieldGroup>
                <DetailFields fieldList={[pair[1]]} valueAs="div" />
              </FieldGroup>
            )}
          </React.Fragment>
        ))}
      </ResponsiveGrid>
      {lastChanged && (
        <LastUpdatedSection>
          <LastUpdatedTitle>Last updated contacts</LastUpdatedTitle>
          <div className="mute">
            {formatLastUpdated(lastChanged.email, 'Email address')}
            {formatLastUpdated(lastChanged.phone, 'Phone number')}
            {formatLastUpdated(lastChanged.address, 'Mailing address(es)')}
          </div>
        </LastUpdatedSection>
      )}
    </>
  );
};
